import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import {DataService} from '../../../../Services/data.service';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { TaxRateModel } from '../../../../DataModels/tax-rate-model';
import {TaxModel} from '../../../../DataModels/tax-model';
import { DatePipe } from '@angular/common';
import { ResponseModel } from '../../../../DataModels/response-model';
import { NotificationService } from '../../../../Services/notification.service';
import { ConfirmDialogService } from '../../../../Services/confirm-dialog.service';
import { Observable } from 'rxjs';
import { BsToastService} from '../../../../Services/bs-toast-service';
import {Title} from '@angular/platform-browser';

@Component({
  selector: 'app-tax-rate-form',
  templateUrl: './tax-rate-form.component.html',
  styleUrls: ['./tax-rate-form.component.css'],
  standalone: false
})
export class TaxRateFormComponent implements OnInit {
  taxRateID;
  taxRate: TaxRateModel;
  taxRateForm: UntypedFormGroup;

  taxRateCount = 0;
  taxCount = 0;
  taxList: TaxModel[];
  paid = false;
  showDelete = false;
  submitted = false;
  isLoaded = false;
  isTaxLoaded = false;
  taxRates$: Observable<TaxRateModel[]>;
  isSaveNew = false;
  originalSnapshot: any = {};

  buttonConfig: any = {
    save: true,
    close: true,
    reset: true,
    delete: true,
    saveNew: true,
    saveClose: true,
    print: false,
  };

  constructor(
    private table: DataService,
    private notification: BsToastService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private confirmDialogService: ConfirmDialogService,
    private titleService: Title
  ) {
    this.createForm();
    this.taxRateID = this.route.snapshot.params.id;
  }

  createForm() {
    const pipe = new DatePipe('en-US');
    this.taxRateForm = this.formBuilder.group({
      tax_id: new UntypedFormControl(''),
      name: new UntypedFormControl(''),
      rate: new UntypedFormControl(''),
      from_date: new UntypedFormControl(null),
      to_date: new UntypedFormControl(null),
      active: new UntypedFormControl(true),
    });
  }

  ngOnInit() {
    this.titleService.setTitle($localize`:Tax Rate@@taxRate:Tax Rate`);
    this.loadTaxes();
    if (this.taxRateID) {
      this.LoadData();
      this.buttonConfig.reset = false;
    } else {
      Promise.resolve().then(() => {
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
      });
    }
  }

  private LoadData() {
    this.table.getData('tax_rate', this.taxRateID).subscribe({
      next: (result: any) => {
        // Handle both wrapped { Data: T } and raw T responses
        const data = (result && result.Data !== undefined) ? result.Data : result;
        this.taxRate = data as TaxRateModel;
        this.taxRateForm.patchValue(this.taxRate);
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
      },
      error: (error: HttpErrorResponse) => {
        // this.notification.error($localize`:Error loading Tax Rates @@errorloadingTaxRates:Error loading Tax Rates`);
        console.log(error);
      }
    });
  }

  private loadTaxes() {
    this.table.getApiListData('tax', '?pageNumber=0&pageSize=999999').subscribe({
      next: (result: ResponseModel) => {
        this.taxList = result.Data as TaxModel[];
        this.taxCount = this.taxList.length;
        this.isLoaded = true;
      },
      error: (error: HttpErrorResponse) => {
        // this.notification.error($localize`:Error loading Tax Rates @@errorloadingTaxRates:Error loading Tax Rates`);
        console.log(error);
      },
      complete: () => {
      }
    });
  }

  saveClose() {
    if (this.onSave(true)) {
    }
  }

  saveNew() {
    // Defer reset until async save completes inside onSave(false)
    this.isSaveNew = true;
    this.onSave(false);
  }

  resetForm() {
    this.resetToDefaults();
    try {
      this.originalSnapshot = this.buildSnapshot();
    } catch {
    }
  }

  onClose() {
    this.router.navigate(['admin/tax-rate-list']).then();
  }

  private resetToDefaults(): void {
    // Reset form to known defaults and prepare component for a fresh new entry
    this.taxRateForm.reset({
      tax_id: '',
      name: '',
      rate: '',
      from_date: null,
      to_date: null,
      active: true,
    });
    this.taxRate = {tax_id: '', name: '', rate: '', from_date: null, to_date: null, active: true} as unknown as TaxRateModel;
    this.taxRateID = null;
    this.submitted = false;
    try {
      this.originalSnapshot = this.buildSnapshot();
    } catch {
    }
  }

  deleteItem() {
    let retMsg = '';
    const messages: string[] = [
      $localize`:Are you sure you want to delete tax rate? @@areYouSureYouWantToDeleteTaxrate:Are you sure you want to delete tax rate?`,
      this.taxRate.tax_id,
    ];
    this.confirmDialogService.confirmThis(
      messages,
      () => {
        this.table.deleteDataById('tax_rate', this.taxRateID).subscribe({
          next: (result) => {
            retMsg = result;
            // this.notification.success($localize`:Deleted @@deleted:Deleted`);
          },
          error: (error: HttpErrorResponse) => {
            // this.notification.error($localize`:Error @@error:Error`);
            console.log(error);
          },
          complete: () => {
            this.onClose();
          }
        });
      },
      () => {
        this.notification.showSuccessToast($localize`:Delete Canceled@@deleteCanceled:Delete Canceled`);
      }
    );
  }

  onSave(saveClose: boolean) {
    this.submitted = true;

    if (this.taxRateForm.invalid) {
      this.notification.showWarningToast($localize`:Form Errors! @@formErrors:Form Errors!`);
      return false;
    }

    this.taxRate = Object.assign({}, this.taxRateForm.value);

    if (this.taxRateID) {
      // Update existing
      this.table.patch('tax_rate', this.taxRateID, this.taxRate).then(
        () => {
          try {
            this.originalSnapshot = this.buildSnapshot();
          } catch {
          }
          if (saveClose) {
            this.onClose();
          } else if (this.isSaveNew) {
            this.isSaveNew = false;
            this.resetToDefaults();
          }
        },
        (error: HttpErrorResponse) => {
          console.log(error);
        }
      ).finally(() => {
        this.submitted = false;
      });
    } else {
      // Create new
      this.table.postWithPromise('tax_rate', this.taxRate).then((res) => {
        let apiReturn: any = null;
        if (typeof res === 'object') {
          apiReturn = JSON.parse(JSON.stringify(res));
        }
        this.taxRateID = apiReturn?.id;
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
        if (saveClose) {
          this.onClose();
        } else if (this.isSaveNew) {
          this.isSaveNew = false;
          this.resetToDefaults();
        }
      }).catch((error: HttpErrorResponse) => {
        console.log(error);
      }).finally(() => {
        this.submitted = false;
      });
    }

    return true;
  }

  buildSnapshot(): any {
    try {
      return this.taxRateForm.getRawValue();
    } catch {
      return {};
    }
  }

  hasChanges(): boolean {
    try {
      const current = this.buildSnapshot();
      const original = this.originalSnapshot || {};
      return JSON.stringify(current) !== JSON.stringify(original);
    } catch {
      return false;
    }
  }

  checkForChanges(): boolean {
    return this.hasChanges();
  }
}
